import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Button,
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  Input,
  Label,
} from "@devhop/ui";
import { useState } from "react";
import {
  useSubstituteInstructorCheckInClassSessionMutation,
  useSubstituteInstructorCheckInPrivateBookingMutation,
} from "../api/queries";

type AddSubstituteInstructorDialogProps = {
  sessionId: string;
  sessionType: "general_class" | "private_lesson";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  assignedInstructorIds?: string[];
  assignedInstructorNames?: string[];
};

export function AddSubstituteInstructorDialog({
  sessionId,
  sessionType,
  open,
  onOpenChange,
  onSuccess,
  assignedInstructorIds = [],
  assignedInstructorNames = [],
}: AddSubstituteInstructorDialogProps) {
  const [instructorId, setInstructorId] = useState<string>("");
  const [substituteForInstructorId, setSubstituteForInstructorId] =
    useState<string>("");
  const [substituteReason, setSubstituteReason] = useState<string>("");

  const substituteClassMutation =
    useSubstituteInstructorCheckInClassSessionMutation();
  const substitutePrivateMutation =
    useSubstituteInstructorCheckInPrivateBookingMutation();

  const isSubmitting =
    substituteClassMutation.isPending || substitutePrivateMutation.isPending;

  const handleSubmit = async () => {
    if (!instructorId) return;

    try {
      if (sessionType === "general_class") {
        await substituteClassMutation.mutateAsync({
          sessionId,
          instructorId,
          substituteForInstructorId: substituteForInstructorId || null,
          substituteReason: substituteReason || null,
        });
      } else {
        await substitutePrivateMutation.mutateAsync({
          bookingId: sessionId,
          instructorId,
          substituteForInstructorId: substituteForInstructorId || null,
          substituteReason: substituteReason || null,
        });
      }
      handleClose();
      onSuccess();
    } catch {
      // Error handling is done in mutation
    }
  };

  const handleClose = () => {
    setInstructorId("");
    setSubstituteForInstructorId("");
    setSubstituteReason("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className="max-w-md gap-4 sm:max-w-md">
          <DialogTitle className="font-semibold text-lg">
            ເພີ່ມຄູສອນແທນ
          </DialogTitle>

          <div className="space-y-4">
            <div>
              <Label
                htmlFor="instructor"
                className="mb-1.5 block font-medium text-sm"
              >
                ຄູສອນທີ່ມາສອນແທນ (ຄົນໃໝ່) *
              </Label>
              <p className="mb-2 text-muted-foreground text-xs">
                ຄົ້ນແລ້ວເລືອກຄູສອນໃຕ້ນີ້ — ໜ້າລຸ່ມ &quot;ແທນຄູສອນ&quot; ແມ່ນເລືອກວ່າແທນໃຜຈາກຊົ່ວໂມງຮຽນ ເທົ່ານັ້ນ
                ຈຶ່ງຈະບັນທຶກໄດ້
              </p>
              <InfiniteCombobox<{ id: string; name: string }>
                queryKey={["master", "instructors", "lookup"]}
                queryFn={async (params: {
                  search: string;
                  pageParam: number;
                }) => {
                  const limit = 20;
                  const skip = (params.pageParam - 1) * limit;
                  const url = new URL(
                    `${config.apiUrl}/master/instructors/lookup`,
                  );
                  url.searchParams.set("limit", String(limit));
                  url.searchParams.set("skip", String(skip));
                  if (params.search) url.searchParams.set("q", params.search);
                  const data = await fetchLookupForInfinite<{
                    id: string;
                    name: string;
                  }>(url.toString(), params);
                  return data;
                }}
                preloadQueryFn={(id) =>
                  hydrateLookupItem<{ id: string; name: string }>(
                    `${config.apiUrl}/master/instructors/lookup`,
                    id,
                  )
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                value={instructorId}
                onValueChange={setInstructorId}
                placeholder="ຄົ້ນຫາຄູສອນ..."
                className="w-full"
                clearable
              />
            </div>

            <div>
              <Label
                htmlFor="substitute-for"
                className="mb-1.5 block font-medium text-sm"
              >
                ແທນຄູສອນ (ບໍ່ບັງຄັບ)
              </Label>
              {assignedInstructorIds.length > 0 ? (
                <SimpleSelect
                  value={substituteForInstructorId || undefined}
                  onValueChange={(val) =>
                    setSubstituteForInstructorId(val || "")
                  }
                  placeholder="ເລືອກຄູສອນທີ່ຖືກແທນ..."
                  className="w-full"
                  clearable
                  options={assignedInstructorIds.map((id, index) => ({
                    value: id,
                    label: assignedInstructorNames[index] || id,
                  }))}
                />
              ) : (
                <Input
                  id="substitute-for"
                  placeholder="ບໍ່ມີຄູສອນທີ່ຖືກກຳນົດໄວ້"
                  disabled
                  className="w-full"
                />
              )}
            </div>

            <div>
              <Label
                htmlFor="reason"
                className="mb-1.5 block font-medium text-sm"
              >
                ເຫດຜົນ (ບໍ່ບັງຄັບ)
              </Label>
              <textarea
                id="reason"
                placeholder="ເຊັ່ນ ຄູເກົ່າປ່ວຍ, ຄູເກົ່າຕິດທຸລະກິດ..."
                value={substituteReason}
                onChange={(e) => setSubstituteReason(e.target.value)}
                className="flex min-h-[80px] w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              ຍົກເລີກ
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!instructorId || isSubmitting}
            >
              {isSubmitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
